'use client'

import type { ProductPriceProps } from '@maestro/commerce-blocks/components/product-price/product-price.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { Price } from '@maestro/commerce-blocks/components/price'
import { useProductPrice } from '@maestro/commerce-blocks/components/product-price/batons/use-product-price'

import '@maestro/commerce-blocks/components/product-price/styles/product-price.scss'

export default function ProductPrice(props: ProductPriceProps) {
  const {
    price,
    forceShowDecimals,
    unitLabel,
    isMemberSimplified = false,
    isLoading = false,
    showTaxLabel,
  } = props
  const { isDiscountedPrice, isMemberPrice } = useProductPrice(props)
  const t = useTranslations('commerce-blocks.price')

  const nonMemberPrice = useMemo(() => {
    return (
      <span
        className={classNames('product-price', {
          'product-price--rebated': isDiscountedPrice,
        })}
      >
        {isDiscountedPrice && (
          <Price
            price={price}
            forceShowDecimals={forceShowDecimals}
            unitLabel={unitLabel}
            isActive
            isDiscountedPrice
            showTaxLabel={showTaxLabel}
            accessibleLabel={t('current-price')}
          />
        )}
        <Price
          price={price}
          forceShowDecimals={forceShowDecimals}
          unitLabel={unitLabel}
          isActive={!isDiscountedPrice}
          showTaxLabel={showTaxLabel}
          accessibleLabel={isDiscountedPrice ? t('original-price') : t('price')}
        />
      </span>
    )
  }, [isDiscountedPrice, price, forceShowDecimals, unitLabel, showTaxLabel, t])

  if (isLoading) {
    return (
      <span className="product-price">
        <div className="product-price__wrapper" role="text"></div>
      </span>
    )
  }

  if (isMemberPrice) {
    if (isMemberSimplified) {
      return (
        <span className="product-price product-price--rebated">
          <Price
            price={price}
            forceShowDecimals={forceShowDecimals}
            unitLabel={unitLabel}
            isActive
            isMemberPrice
            showTaxLabel={showTaxLabel}
            accessibleLabel={t('member-price')}
          />
          <Price
            price={price}
            forceShowDecimals={forceShowDecimals}
            unitLabel={unitLabel}
            isActive={false}
            showTaxLabel={showTaxLabel}
            accessibleLabel={t('price')}
          />
        </span>
      )
    }
    return (
      <span className={classNames('product-price', 'product-price--member')}>
        <div
          className="product-price__wrapper product-price__wrapper--member"
          role="text"
        >
          <span className="product-price__member-label">
            {t('label-members')}
          </span>
          <Price
            price={price}
            forceShowDecimals={forceShowDecimals}
            unitLabel={unitLabel}
            isMemberPrice
            showTaxLabel={showTaxLabel}
            accessibleLabel={t('member-price')}
          />
        </div>
        <div
          className="product-price__wrapper product-price__wrapper--non-member"
          role="text"
        >
          <span className="product-price__member-label">
            {t('label-non-members')}
          </span>
          {nonMemberPrice}
        </div>
      </span>
    )
  }

  return nonMemberPrice
}
